import { createContext, useContext, useState, useEffect, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("jvc_token"));
  const [isImpersonating, setIsImpersonating] = useState(!!localStorage.getItem("jvc_original_token"));

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(`${API}/auth/me`, { credentials: "include", headers });
      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
      } else {
        setUser(null);
        localStorage.removeItem("jvc_token");
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
      credentials: "include"
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || "Erreur de connexion");
    }
    const data = await res.json();
    localStorage.setItem("jvc_token", data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password, company_name = "", siret = "") => {
    const res = await fetch(`${API}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, email, password, company_name, siret }),
      credentials: "include"
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || "Erreur d'inscription");
    }
    const data = await res.json();
    localStorage.setItem("jvc_token", data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    localStorage.removeItem("jvc_original_token");
    setIsImpersonating(false);
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    localStorage.removeItem("jvc_token");
    setToken(null);
    setUser(null);
  };

  const impersonate = (newToken, newUser) => {
    const currentToken = localStorage.getItem("jvc_token");
    localStorage.setItem("jvc_original_token", currentToken);
    localStorage.setItem("jvc_token", newToken);
    setToken(newToken);
    setUser(newUser);
    setIsImpersonating(true);
  };

  const stopImpersonation = () => {
    const originalToken = localStorage.getItem("jvc_original_token");
    if (originalToken) {
      localStorage.setItem("jvc_token", originalToken);
      localStorage.removeItem("jvc_original_token");
      setToken(originalToken);
      setIsImpersonating(false);
      // Reload to get the original user
      window.location.reload();
    }
  };

  const apiFetch = async (url, options = {}) => {
    const headers = { "Content-Type": "application/json", ...options.headers };
    const currentToken = localStorage.getItem("jvc_token");
    if (currentToken) headers["Authorization"] = `Bearer ${currentToken}`;
    const res = await fetch(url, { ...options, headers, credentials: "include" });
    if (res.status === 401) {
      setUser(null);
      localStorage.removeItem("jvc_token");
    }
    return res;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, loginWithGoogle, logout, token, apiFetch, API, impersonate, stopImpersonation, isImpersonating }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
